import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useChapter, useUpdateChapter } from '@/hooks/useChapters';
import { SidebarHeader } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { FileText, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface ChapterOutlineTabProps {
  chapterId?: string | number;
}

export function ChapterOutlineTab({ chapterId }: ChapterOutlineTabProps) {
  const { toast } = useToast();
  const { projectId } = useParams();
  const [content, setContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const { data: chapter, isLoading } = useChapter(chapterId);
  const updateChapterMutation = useUpdateChapter();

  useEffect(() => {
    if (chapter) {
      setContent(chapter.description || '');
      setHasChanges(false);
    }
  }, [chapter]);

  const handleContentChange = (value: string) => {
    setContent(value);
    setHasChanges(value !== (chapter?.description || ''));
  };

  const handleSave = async () => {
    if (!chapterId || !chapter || !projectId) return;

    try {
      await updateChapterMutation.mutateAsync({
        chapterId: Number(chapterId),
        title: chapter.title, // Keep existing title
        description: content,
        projectId: projectId
      });

      setHasChanges(false);
      toast({
        title: "Saved",
        description: "Description updated successfully",
      });
    } catch (error) {
      console.error('Error saving description:', error);
      toast({
        title: "Error",
        description: "Failed to save chapter description",
        variant: "destructive",
      });
    }
  };

  const renderContent = () => {
    if (!chapterId) {
      return (
        <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground min-h-0">
          Select a chapter to view its description/outline
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground min-h-0">
          <span className="animate-pulse">Loading description...</span>
        </div>
      );
    }

    return (
      <>
        <div className="flex-1 overflow-hidden relative p-0 min-h-0">
          <Textarea
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Write your chapter description / outline here..."
            className="w-full h-full resize-none border-0 rounded-none p-5 text-sm leading-relaxed bg-transparent focus-visible:ring-0 placeholder:text-muted-foreground/40 custom-scrollbar"
            spellCheck={false}
          />
          {/* Gradient fade at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent pointer-events-none" />
        </div>

        {/* Floating Toolbar Design */}
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <div className={cn(
            "flex items-center justify-between px-3 py-2 rounded-xl backdrop-blur-md border border-border/40 shadow-lg transition-all duration-300 transform",
            hasChanges ? "bg-background/80 translate-y-0 opacity-100" : "bg-transparent border-transparent shadow-none translate-y-2 opacity-0 pointer-events-none"
          )}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="size-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span>Unsaved changes</span>
            </div>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={updateChapterMutation.isPending}
              className="h-7 px-3 text-xs gap-1.5 bg-primary/90 hover:bg-primary shadow-sm"
            >
              <Save className="size-3" />
              {updateChapterMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden relative group bg-transparent">
      <SidebarHeader className="h-12 flex flex-row items-center justify-between border-b px-4 py-0 shrink-0">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <FileText className="h-4 w-4" />
          <span>Chapter Outline</span>
        </div>
      </SidebarHeader>
      {renderContent()}
    </div>
  );
}
